// pages/OrdersPage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { useModal } from '../context/ModalContext.js';
import API from '../api/MainApi.js';
import OrderList from '../components/OrderList.jsx';
import OrderForm from '../components/OrderForm.jsx';
import './OrdersPage.css';

const OrdersPage = () => {
    const { hasPermission } = useAuth();
    const { showConfirm, showAlert } = useModal();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);

    const canManageOrders = hasPermission('canManageOrders');
    const canEditOrders = hasPermission('canEditOrders');
    const canDeleteOrders = hasPermission('canDeleteOrders');

    useEffect(() => {
        fetchOrders();
    }, [filterStatus]);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const response = await API.getOrders(filterStatus);
            setOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching orders:', error);
            showAlert('Ошибка', 'Не удалось загрузить заказы', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOrder = async (orderData) => {
        try {
            await API.createOrder(orderData);
            showAlert('Успешно', 'Заказ создан', 'success');
            fetchOrders();
            setShowForm(false);
        } catch (error) {
            console.error('Error creating order:', error);
            showAlert('Ошибка', error.response?.data?.message || 'Не удалось создать заказ', 'error');
        }
    };

    const handleUpdateOrder = async (id, orderData) => {
        try {
            await API.updateOrder(id, orderData);
            showAlert('Успешно', 'Заказ обновлён', 'success');
            fetchOrders();
            setEditingOrder(null);
            setShowForm(false);
        } catch (error) {
            console.error('Error updating order:', error);
            showAlert('Ошибка', error.response?.data?.message || 'Не удалось обновить заказ', 'error');
        }
    };

    const handleDeleteOrder = (id) => {
        showConfirm('Удаление заказа', 'Вы уверены, что хотите удалить этот заказ?', async () => {
            try {
                await API.deleteOrder(id);
                showAlert('Успешно', 'Заказ удалён', 'success');
                fetchOrders();
            } catch (error) {
                console.error('Error deleting order:', error);
                showAlert('Ошибка', error.response?.data?.message || 'Не удалось удалить заказ', 'error');
            }
        });
    };

    const handleEditOrder = (order) => {
        // order уже содержит все данные, включая вложенный объект user
        setEditingOrder(order);
        setShowForm(true);
    };

    const handleStatusChange = (id, status) => {
        showConfirm('Изменение статуса', `Изменить статус заказа на "${status}"?`, async () => {
            try {
                await API.updateOrderStatus(id, status);
                showAlert('Успешно', 'Статус изменён', 'success');
                fetchOrders();
            } catch (error) {
                console.error('Error updating status:', error);
                showAlert('Ошибка', error.response?.data?.message || 'Не удалось изменить статус', 'error');
            }
        });
    };

    const statusOptions = [
        { value: '', label: 'Все' },
        { value: 'new', label: 'Новые' },
        { value: 'accepted', label: 'Принятые' },
        { value: 'shipped', label: 'Отправленные' },
        { value: 'completed', label: 'Завершённые' },
        { value: 'cancelled', label: 'Отменённые' }
    ];

    return (
        <div className="orders-page">
            <div className="page-header">
                <h2>Управление заказами</h2>
                {canManageOrders && (
                    <button className="btn btn-primary" onClick={() => { setEditingOrder(null); setShowForm(true); }}>
                        Создать заказ
                    </button>
                )}
            </div>

            <div className="filters">
                <label>Фильтр по статусу:</label>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                    {statusOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <span className="orders-count">Найдено заказов: {orders.length}</span>
            </div>

            {showForm && (
                <OrderForm
                    order={editingOrder}
                    onSubmit={editingOrder ? (data) => handleUpdateOrder(editingOrder._id, data) : handleCreateOrder}
                    onClose={() => { setShowForm(false); setEditingOrder(null); }}
                />
            )}

            <OrderList
                orders={orders}
                loading={loading}
                canEdit={canEditOrders}
                canDelete={canDeleteOrders}
                onEdit={handleEditOrder}
                onDelete={handleDeleteOrder}
                onStatusChange={handleStatusChange}
            />
        </div>
    );
};

export default OrdersPage;